import {getCurrentScriptUrlContext} from './getCurrentScriptUrlContext';
import {isDevEnvironment} from './isDevEnvironment';
import {getDevLogger} from './getLogger';

const devLogger = getDevLogger();

export type ServiceEnvironment =
  | 'devbox'
  | 'tunnel'
  | 'turbo'
  | 'qa'
  | 'preprod'
  | 'prod';

let runtimeServiceEnvironmentOverride: string | undefined;

export const setServiceEnvironmentOverride = (override: string | undefined) => {
  runtimeServiceEnvironmentOverride = override;
};

export const getServiceEnvironmentOverride = (): string | undefined =>
  runtimeServiceEnvironmentOverride;

export const calculateDevboxPrefix = (): string => {
  const devboxPrefix = process.env.DEVBOX_QUALIFIER || process.env.QUALIFIER;
  if (devboxPrefix) {
    // Regular devboxes have a qualifier now
    // trailhead.corp.stripe.com/docs/devbox/working-with-services/new-all-devboxes-must-have-a-unique-qualifier
    const prefix = `${process.env.USER}-${devboxPrefix}-`;
    // jorgea-0-g6v1--manage-mydev.dev.stripe.me
    return prefix;
  }

  const scriptDevboxPrefix = getCurrentScriptUrlContext().devboxPrefix;
  if (scriptDevboxPrefix) {
    // Fall back to the devbox prefix parsed from the connect.js script URL
    // when the env variable is not available.
    return `${process.env.USER}-${scriptDevboxPrefix}-`;
  }

  // Some older devboxes don't have a qualifier, so we still support this
  return `${process.env.USER}-`;
};

export const getTunnelHost = (service: string): string =>
  `${process.env.USER}-${service}.tunnel.stripe.me`;

export const getDevboxHost = (service: string): string =>
  `${calculateDevboxPrefix()}-${service}-mydev.dev.stripe.me`;

// Return a PRR URL for services exposed via the edge stack (clusterfe/ingress)
export const getTurboHost = (service: string): string =>
  `${calculateDevboxPrefix()}-${service}-dev.dev.stripe.me`;

export const getTargetEnvironment = (
  targetEnvironment: ServiceEnvironment,
  {
    environmentOverride = runtimeServiceEnvironmentOverride ??
      process.env.SERVICE_ENVIRONMENT,
    isDevelopmentBuild = isDevEnvironment,
  }: {
    environmentOverride?: string | undefined;
    isDevelopmentBuild?: boolean;
  } = {},
): ServiceEnvironment => {
  // Do not allow environments to overridden in the production distribution
  if (!isDevelopmentBuild || !environmentOverride) {
    return targetEnvironment;
  }

  const lowercaseOverride = environmentOverride.toLowerCase();
  switch (lowercaseOverride) {
    case 'tunnel':
    case 'devbox':
    case 'qa':
    case 'preprod':
    case 'prod':
    case 'turbo':
      return lowercaseOverride;
    default:
      devLogger.error(
        'Devbox only target override is not a recognized value',
        environmentOverride,
      );
      return targetEnvironment;
  }
};
